import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getTeamDataModel } from 'helpers/ScheduleHelpers';
import { sortScheduledMatchesByStartingDate } from 'helpers/SortingHelpers';
import { memoize } from 'proxy-memoize';
import { RootState } from 'slices/store';
import { Maybe } from 'types/App.types';
import { ScheduledMatch, TeamModel } from 'types/Schedule.types';

export type MatchType = 'None' | 'New' | 'Existing';

export type ActiveMatchStatus = 'None' | 'Modifying' | 'Saving' | 'SaveSuccess' | 'SaveError';

export type ActiveMatchState = {
  type: MatchType;
  status: ActiveMatchStatus;
};

export type SqScheduleProps = {
  teams: TeamModel[];
  teamsLoaded: boolean;
  matches: ScheduledMatch[];
  matchesLoaded: boolean;
  activeMatch?: ScheduledMatch;
  activeMatchState: ActiveMatchState;
};

const initialState: SqScheduleProps = {
  teams: [],
  matches: [],
  teamsLoaded: false,
  matchesLoaded: false,
  activeMatchState: { type: 'None', status: 'None' },
  activeMatch: undefined,
};

export const sqScheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    setTeams: (state, action: PayloadAction<TeamModel[]>) => {
      state.teams = action.payload;
      state.teamsLoaded = action.payload && action.payload.length === 32;
    },
    setTeamsLoaded: (state, action: PayloadAction<boolean>) => {
      state.teamsLoaded = action.payload;
    },
    setMatches: (state, action: PayloadAction<ScheduledMatch[]>) => {
      state.matches = action.payload;
      state.matchesLoaded = true;
    },
    addMatch: (state, action: PayloadAction<ScheduledMatch>) => {
      state.matches = [...state.matches, action.payload];
    },
    updateMatch: (state, action: PayloadAction<ScheduledMatch>) => {
      const index = state.matches.findIndex((m) => m.matchId === action.payload.matchId);
      if (index > -1) {
        const temp = state.matches.slice();
        temp.splice(index, 1, action.payload);
        temp.sort(sortScheduledMatchesByStartingDate);
        state.matches = temp;
      }
    },
    setActiveMatch: (state, action: PayloadAction<Maybe<ScheduledMatch>>) => {
      state.activeMatch = action.payload;
    },
    updateActiveMatch: (state, action: PayloadAction<ScheduledMatch>) => {
      state.activeMatch = { ...state.activeMatch, ...action.payload };
    },

    updateActiveMatchState: (state, action: PayloadAction<ActiveMatchState>) => {
      state.activeMatchState = { ...state.activeMatchState, ...action.payload };
    },

    // RESET ******************************************************************
    resetSchedule: () => {
      return { ...initialState };
    },
  },
});

export const {
  setTeams,
  setMatches,
  addMatch,
  updateMatch,
  setActiveMatch,
  updateActiveMatch,
  updateActiveMatchState,
  resetSchedule,
} = sqScheduleSlice.actions;

export const getTeams = (state: RootState): TeamModel[] => state.schedule.teams;
export const isTeamsLoaded = (state: RootState): boolean => state.schedule.teamsLoaded;

export const getMatches = (state: RootState): ScheduledMatch[] => state.schedule.matches;
export const isMatchesLoaded = (state: RootState): boolean => state.schedule.matchesLoaded;
export const getEnhancedMatches = memoize((state: RootState): ScheduledMatch[] => {
  const matchesFromFB = state.schedule.matches;
  const teams = state.schedule.teams;
  if (matchesFromFB.length > 0 && teams.length) {
    const enhancedMatches: ScheduledMatch[] = matchesFromFB.map((match) => {
      const home = getTeamDataModel(teams[parseInt(match.homeTeamId) - 1] as TeamModel);
      const away = getTeamDataModel(teams[parseInt(match.awayTeamId) - 1] as TeamModel);
      return { ...match, home: { ...match.home, ...home }, away: { ...match.away, ...away } };
    });

    enhancedMatches.sort(sortScheduledMatchesByStartingDate);
    return enhancedMatches;
  }
  return matchesFromFB;
});

export const getActiveMatch = (state: RootState): Maybe<ScheduledMatch> => state.schedule.activeMatch;
export const getActiveMatchState = (state: RootState): ActiveMatchState => state.schedule.activeMatchState;
export const getActiveMatchType = (state: RootState): MatchType => state.schedule.activeMatchState.type;
export const getActiveMatchStatus = (state: RootState): ActiveMatchStatus => state.schedule.activeMatchState.status;

export default sqScheduleSlice.reducer;
